import { useEffect, useMemo, useRef, useState } from 'react';
import type { ForecastDaySummary, ForecastHourSummary, ForecastVariable } from './WeatherDashboard';
import {
  buildDayChartPoints,
  buildWeekChartPoints,
  CHART_THEMES,
  computeYDomain,
  formatChartValue,
  pickAxisLabels,
  type ChartPoint,
} from './weatherChartUtils';

export type OutlookChartMode = 'week' | 'day';

interface WeatherOutlookChartProps {
  variable: ForecastVariable;
  mode: OutlookChartMode;
  days: ForecastDaySummary[];
  hours: ForecastHourSummary[];
  periods: string[];
  selectedPeriod: string | null;
  loading?: boolean;
}

const DEFAULT_WIDTH = 360;
const HEIGHT = 140;
const PAD = { top: 26, right: 16, bottom: 26, left: 16 };

function buildPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
}

function buildAreaPath(points: { x: number; y: number }[], baseline: number): string {
  if (points.length === 0) return '';
  const line = buildPath(points);
  const last = points[points.length - 1]!;
  const first = points[0]!;
  return `${line} L ${last.x.toFixed(2)} ${baseline.toFixed(2)} L ${first.x.toFixed(2)} ${baseline.toFixed(2)} Z`;
}

function layoutPoints(
  chartPoints: ChartPoint[],
  domain: [number, number],
  innerW: number,
  innerH: number,
  x0: number,
  y0: number,
): { plotted: { x: number; y: number; point: ChartPoint }[]; baseline: number } {
  const [yMin, yMax] = domain;
  const span = yMax - yMin || 1;
  const baseline = y0 + innerH;
  const count = chartPoints.length;

  const plotted = chartPoints.map((point, index) => {
    const x = count <= 1 ? x0 + innerW / 2 : x0 + (index / (count - 1)) * innerW;
    const value = point.value;
    const y =
      value == null ? baseline : y0 + innerH - ((value - yMin) / span) * innerH;
    return { x, y, point };
  });

  return { plotted, baseline };
}

/** Avoid overlapping value labels — fewer on narrow screens / dense series. */
function pickValueLabelIndices(
  validPlotted: { x: number; y: number; point: ChartPoint }[],
  chartWidth: number,
): Set<number> {
  const indices = new Set<number>();
  if (validPlotted.length === 0) return indices;

  const minSpacing = chartWidth < 380 ? 56 : chartWidth < 520 ? 44 : 36;
  const maxLabels = Math.max(2, Math.floor(chartWidth / minSpacing));

  if (validPlotted.length <= maxLabels) {
    validPlotted.forEach((_, i) => indices.add(i));
    return indices;
  }

  indices.add(0);
  indices.add(validPlotted.length - 1);

  let lastX = validPlotted[0]!.x;
  for (let i = 1; i < validPlotted.length - 1; i++) {
    const point = validPlotted[i]!;
    if (point.x - lastX >= minSpacing) {
      indices.add(i);
      lastX = point.x;
    }
  }

  return indices;
}

function maxAxisLabels(chartWidth: number, mode: OutlookChartMode, pointCount: number): number {
  if (mode === 'week') {
    if (chartWidth < 340) return Math.min(4, pointCount);
    if (chartWidth < 420) return Math.min(5, pointCount);
    return Math.min(7, pointCount);
  }
  if (chartWidth < 340) return Math.min(5, pointCount);
  if (chartWidth < 480) return Math.min(6, pointCount);
  return Math.min(8, pointCount);
}

export function WeatherOutlookChart({
  variable,
  mode,
  days,
  hours,
  periods,
  selectedPeriod,
  loading,
}: WeatherOutlookChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(DEFAULT_WIDTH);
  const theme = CHART_THEMES[variable];

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const next = Math.max(280, Math.round(el.clientWidth));
      setChartWidth((prev) => (prev === next ? prev : next));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const chartPoints = useMemo(() => {
    if (mode === 'day' && selectedPeriod) {
      return buildDayChartPoints(variable, hours, selectedPeriod);
    }
    return buildWeekChartPoints(variable, periods, days);
  }, [mode, variable, days, hours, periods, selectedPeriod]);

  const numericValues = chartPoints
    .map((p) => p.value)
    .filter((v): v is number => v != null);

  const domain = useMemo(
    () => computeYDomain(numericValues, variable),
    [numericValues, variable],
  );

  const layoutWidth =
    mode === 'day'
      ? Math.max(chartWidth, chartPoints.length * 44)
      : chartWidth;
  const needsScroll = mode === 'day' && layoutWidth > chartWidth;

  const innerW = layoutWidth - PAD.left - PAD.right;
  const innerH = HEIGHT - PAD.top - PAD.bottom;
  const { plotted, baseline } = layoutPoints(
    chartPoints,
    domain,
    innerW,
    innerH,
    PAD.left,
    PAD.top,
  );

  const validPlotted = plotted.filter((p) => p.point.value != null);
  const linePath = buildPath(validPlotted);
  const areaPath = buildAreaPath(validPlotted, baseline);
  const axisLabels = pickAxisLabels(
    chartPoints,
    maxAxisLabels(layoutWidth, mode, chartPoints.length),
  );

  const labelIndices = new Set(
    axisLabels.map((l) => chartPoints.findIndex((p) => p.key === l.key)),
  );

  const valueLabelIndices = pickValueLabelIndices(validPlotted, layoutWidth);
  const valueFontSize = layoutWidth < 380 ? 9 : 10;
  const axisFontSize = layoutWidth < 380 ? 9 : 10;

  const empty = !loading && numericValues.length === 0;
  const gradientId = `outlook-${variable}-fill`;

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${needsScroll ? 'overflow-x-auto [scrollbar-width:thin]' : ''}`}
      aria-hidden={empty}
    >
      {loading && numericValues.length === 0 ? (
        <div className="flex h-[140px] items-center justify-center">
          <span className="h-full w-full animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800/80" />
        </div>
      ) : empty ? (
        <div className="flex h-[140px] items-center justify-center px-4 text-center text-xs text-gray-500 dark:text-gray-400">
          {mode === 'day'
            ? 'Hourly detail is not available for this day yet.'
            : 'No forecast data for this variable yet.'}
        </div>
      ) : (
        <svg
          viewBox={`0 0 ${layoutWidth} ${HEIGHT}`}
          width={layoutWidth}
          height={HEIGHT}
          className={`block h-[140px] max-w-none ${needsScroll ? '' : 'w-full'}`}
          role="img"
          aria-label={`${variable} ${mode === 'day' ? 'hourly' : '7-day'} forecast chart`}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={theme.fillTop} />
              <stop offset="100%" stopColor={theme.fillBottom} />
            </linearGradient>
          </defs>

          {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />}

          {linePath && (
            <path
              d={linePath}
              fill="none"
              stroke={theme.stroke}
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {validPlotted.map(({ x, y, point }, index) => (
            <g key={point.key}>
              <circle cx={x} cy={y} r={3} fill={theme.dot} stroke="white" strokeWidth={1.25} />
              {valueLabelIndices.has(index) && point.value != null && (
                <text
                  x={x}
                  y={y - 10}
                  textAnchor="middle"
                  className="fill-gray-600 dark:fill-gray-300"
                  style={{ fontSize: valueFontSize, fontWeight: 600 }}
                >
                  {formatChartValue(variable, point.value)}
                </text>
              )}
            </g>
          ))}

          {plotted.map(({ x, point }, index) =>
            labelIndices.has(index) ? (
              <text
                key={`axis-${point.key}`}
                x={x}
                y={HEIGHT - 8}
                textAnchor="middle"
                className="fill-gray-500 dark:fill-gray-400"
                style={{ fontSize: axisFontSize }}
              >
                {point.label}
              </text>
            ) : null,
          )}
        </svg>
      )}
    </div>
  );
}
